import { HTTP_STATUS } from "@api/constant";
import PodcastCard from "@blocks/Card/PodcastCard";
import MainBlock from "@container/MainBlock";
import MainContainer, { AdsSettings } from "@container/MainContainer";
import SideBlock from "@container/SideBlock/SideBlock";
import WrapperBlock from "@container/WrapperBlock";
import Audio from "@elements/Audio/Audio";
import Heading from "@elements/Typography/Heading";
import fetchTMApi from "@helper/fetchTMApi";
import { getGTMData } from "@helper/getGtmData";
import { isClientSide } from "@helper/utils";
import useGlobalAdSetting from "@hooks/useGlobalAdSetting";
import { usePageScroll } from "@hooks/usePageScroll";
import useSwrApi from "@hooks/useSwrApi";
import PodcastMetaData from "@routes/Podcast/PodcastMetaData";
import {
  fetchContext,
  ResponseType,
  TRouteWithRedirect,
} from "@sphtech/web2-core/ssr";
import { createMetaTags } from "@sphtech/web2-seo-schema";
import { type PodcastProcessedResponse } from "@transformer/usePodcastResponse";
import { PageAdTargetingTypeEnum } from "@typings/Ads.d";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ClientLoaderFunctionArgs, useParams } from "react-router";
import { twMerge } from "tailwind-merge";

const sectionName = "podcasts";

export function meta() {
  return createMetaTags(PodcastMetaData(sectionName).metaTags);
}

export default function Podcast() {
  const { data } = useSwrApi<PodcastProcessedResponse[]>("podcasts");
  const { level1: id } = useParams();
  const [podcasts, setPodcasts] = useState<PodcastProcessedResponse[]>();
  const [playing, setPlaying] = useState<
    PodcastProcessedResponse | undefined
  >();
  const adSettings: AdsSettings = useGlobalAdSetting();

  useEffect(() => {
    if (!isClientSide) return;
    const fetchData = async () => {
      if (!data || data.length === 0) return;
      let nowPlaying = false;
      try {
        const podcast: PodcastProcessedResponse[] = id
          ? await fetchTMApi<PodcastProcessedResponse[]>(
              `podcasts/${id}`,
              "GET",
            )
          : data;

        setPlaying(podcast[0]);
        nowPlaying = id ? false : true;
      } catch (error) {
        nowPlaying = true;
        setPlaying(data[0]);
      }

      const updatedPodcasts = data.map((podcast, index) =>
        index === 0
          ? { ...podcast, nowPlaying: nowPlaying ? true : false }
          : podcast,
      );
      setPodcasts(updatedPodcasts);
    };

    void fetchData();
  }, [id, data]);

  const handleNowPlaying = useMemo(
    () => (id: string) => {
      setPodcasts(
        (prevValue) =>
          prevValue &&
          prevValue.map((video) => ({
            ...video,
            nowPlaying: video.id === id,
          })),
      );
    },
    [setPodcasts],
  );

  const handleOnClickThumbnail = useCallback(
    (props: PodcastProcessedResponse) => {
      // eslint-disable-next-line react/prop-types
      const { id } = props;
      handleNowPlaying(id);
      setPlaying(props);
    },
    [handleNowPlaying, setPlaying],
  );

  const pageAdTargetValue = PageAdTargetingTypeEnum.LISTING;

  // sets the initial starting point to 256px, when user scroll the header will stay for a while before it hides.
  const scrollDir = usePageScroll({ initialStart: 256 });

  return (
    <MainContainer
      sectionName={sectionName}
      pageAdTargetValue={pageAdTargetValue}
      suppliedAdsSettings={adSettings}
    >
      <WrapperBlock className="flex flex-col lg:flex-row justify-between">
        <MainBlock
          showTitle={false}
          sectionName={sectionName}
          showBreadcrumbs={true}
        >
          {playing && (
            <Audio
              {...playing}
              className={twMerge(
                "sticky top-0 lg:top-3xl bg-white-100 border-b border-b-gray-1050 transition-all duration-1000 ease-in-out",
                scrollDir == "down" ? "top-[52px]" : "top-[104px]",
              )}
            />
          )}
          <Heading className="text-3xl font-bold font-primary">
            All Episodes
          </Heading>
          <div className="overflow-y-hidden">
            {podcasts &&
              Array.isArray(podcasts) &&
              podcasts.map((podcast) => (
                <button
                  key={podcast.id}
                  onClick={() => {
                    handleOnClickThumbnail(podcast);
                  }}
                >
                  <PodcastCard
                    fullDate={podcast.dateDuration}
                    imageField={{ url: podcast.thumbnail }}
                    title={podcast.name}
                    paragraph={podcast.description}
                    linkTo=""
                    displayWidth={0}
                    displayHeight={0}
                    elements={{ hasDate: true }}
                  />
                </button>
              ))}
          </div>
        </MainBlock>
        <SideBlock
          className="hidden lg:block"
          pageAdTargetValue={pageAdTargetValue}
        />
      </WrapperBlock>
    </MainContainer>
  );
}

export function loader(): Promise<TRouteWithRedirect<object | null, string>> {
  const { gaData } = PodcastMetaData(sectionName);

  return Promise.resolve({
    type: ResponseType.SUCCESS,
    statusCode: HTTP_STATUS.SUCCESS,
    payload: null,
    gtm: getGTMData(gaData),
  });
}

export async function clientLoader(args: ClientLoaderFunctionArgs) {
  const result = (await fetchContext(args)) as { context?: unknown };

  if (!result.context) {
    console.error(
      "Error occured during client-side routing, returning server Loader Data",
    );
    return await args.serverLoader();
  }

  const typedResult: unknown = result.context;

  // Check for valid payload field
  if (
    !typedResult ||
    typeof typedResult !== "object" ||
    !("payload" in typedResult) ||
    !Array.isArray(typedResult.payload)
  ) {
    console.error("Client Loader: fetchContext() returned an invalid payload");
  }
  return typedResult as Promise<
    TRouteWithRedirect<PodcastProcessedResponse, string>
  >;
}
